import React from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import MoveToInboxRounded from '@mui/icons-material/MoveToInboxRounded';
import PhoneInTalkRounded from '@mui/icons-material/PhoneInTalkRounded';
import DoNotDisturbOnRounded from '@mui/icons-material/DoNotDisturbOnRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import { EmptyState, Modal, RevealText, StatusChip, Timeline, ToolbarButton, type TimelineStep } from '../../../ui-kit';
import {
  fetchCall, fetchLeadStory, type CallStatus, type LeadStory, type StoryStep,
} from '../../../services/roccoService';
import { fromNow, inZone, isViewersClock, lengthOf, onDay, useLive, when } from './live';
import AgentName from './AgentName';

/**
 * One lead, start to finish: where it came from, who had it, every call, and
 * who has it now.
 *
 * The point is the change of hands — an AI agent works the lead until a call
 * books a time, then a person keeps the promise — so each step says who, and
 * whether that who is an AI. Watches the lead while open, so a call that ends
 * while you are reading appears in place.
 */
export default function LeadStoryModal({
  leadId,
  callId: openCallId = null,
  onClose,
}: {
  leadId: string;
  /** Open straight onto this call's transcript. */
  callId?: string | null;
  onClose: () => void;
}) {
  const [callId, setCallId] = React.useState<string | null>(openCallId);
  const fetcher = React.useCallback(() => fetchLeadStory(leadId), [leadId]);
  const { data: story, error } = useLive(fetcher);

  if (callId) {
    return <TranscriptModal callId={callId} onBack={() => setCallId(null)} onClose={onClose} />;
  }

  const lead = story?.lead;
  return (
    <Modal
      open
      onClose={onClose}
      title={lead ? lead.name : 'Loading…'}
      subtitle={lead ? (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <RevealText value={lead.phone} what="phone number" />
          <span>
            {[`from ${lead.source}`, lead.arrived_at && `arrived ${fromNow(lead.arrived_at)}`]
              .filter(Boolean).join(' · ')}
          </span>
        </Box>
      ) : undefined}
      size="md"
      footer={<ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>}
    >
      {error && !story ? <EmptyState message={error} /> : null}
      {error && !story ? null : <Timeline steps={story ? toSteps(story, setCallId) : []} loading={!story} />}
    </Modal>
  );
}

const CALL_TONE: Record<CallStatus, TimelineStep['tone']> = {
  ringing: 'brand',
  'in progress': 'brand',
  ended: 'brand',
  callback: 'good',
  connected: 'good',
  'no answer': 'neutral',
  denied: 'bad',
  failed: 'warning',
};

function who(step: { actor?: string | null; actor_kind?: string | null }): string {
  return step.actor_kind === 'ai' ? 'AI agent' : 'Advisor';
}

function toSteps(story: LeadStory, openCall: (id: string) => void): TimelineStep[] {
  const steps: TimelineStep[] = story.steps.map((step: StoryStep, index) => {
    const key = `${step.kind}-${index}`;
    const meta = when(step.at);
    switch (step.kind) {
      case 'arrived':
        return { key, title: step.title, meta, detail: step.detail, marker: <MoveToInboxRounded /> };
      case 'assigned':
      case 'handed_off':
        return {
          key,
          title: step.title,
          meta,
          marker: (step.actor || '?').charAt(0),
          tone: step.actor_kind === 'ai' ? 'brand' : 'neutral',
          detail: step.kind === 'assigned'
            ? `${who(step)} · makes every call until a callback is booked`
            : `${who(step)} · ${step.detail || 'takes the callback'}`,
        };
      case 'call':
        return {
          key,
          title: step.title,
          meta: step.duration_secs ? `${meta} · ${lengthOf(step.duration_secs)}` : meta,
          marker: <PhoneInTalkRounded />,
          tone: CALL_TONE[step.status || 'connected'],
          detail: step.detail,
          action: step.has_transcript && step.call_id ? (
            <ToolbarButton variant="ghost" onClick={() => openCall(step.call_id!)}>
              Read transcript
            </ToolbarButton>
          ) : undefined,
        };
      default:
        return { key, title: step.title, meta, detail: step.detail, marker: <DoNotDisturbOnRounded /> };
    }
  });

  if (story.upcoming) {
    const next = story.upcoming;
    steps.push({
      key: 'upcoming',
      title: next.title,
      // A callback reads as the lead agreed it, zone named, then the viewer's own time if different.
      meta: next.kind === 'callback'
        ? [inZone(next.at, story.lead.callback_tz),
          isViewersClock(next.at, story.lead.callback_tz) ? null : `${onDay(next.at)} your time`,
          fromNow(next.at)].filter(Boolean).join(' · ')
        : `${when(next.at)} · ${fromNow(next.at)}`,
      marker: (next.actor || '?').charAt(0),
      upcoming: true,
      detail: next.kind === 'callback'
        ? `${who(next)} · the time the lead agreed on the call`
        : `${who(next)} · the same voice as last time`,
    });
  }
  return steps;
}

function TranscriptModal({
  callId,
  onBack,
  onClose,
}: {
  callId: string;
  onBack: () => void;
  onClose: () => void;
}) {
  const fetcher = React.useCallback(() => fetchCall(callId), [callId]);
  const { data: call, error } = useLive(fetcher);

  return (
    <Modal
      open
      onClose={onClose}
      title={call ? call.lead : 'Loading…'}
      subtitle={call ? `${when(call.started_at)} · ${lengthOf(call.duration_secs)}` : undefined}
      size="lg"
      actions={call ? <StatusChip tone={call.status === 'denied' ? 'bad'
        : call.status === 'callback' || call.status === 'connected' ? 'good' : 'neutral'}
        label={call.live ? 'on the call' : call.status} /> : undefined}
      footer={
        <Stack direction="row" justifyContent="space-between" sx={{ width: '100%' }}>
          <ToolbarButton variant="ghost" onClick={onBack} icon={<ArrowBackRounded />}>
            Back to the lead
          </ToolbarButton>
          <ToolbarButton variant="ghost" onClick={onClose}>Close</ToolbarButton>
        </Stack>
      }
    >
      {error && !call ? <EmptyState message={error} /> : null}
      {!call && !error ? (
        <Stack spacing={1.25} aria-hidden>
          <Skeleton variant="text" width="35%" />
          <Skeleton variant="text" width="90%" />
          <Skeleton variant="rounded" height={220} />
        </Stack>
      ) : null}
      {call ? (
        <Stack spacing={1.5}>
          {call.agent ? (
            <AgentName person={{ name: call.agent, kind: call.agent_kind }}
              subtitle={call.outcome || undefined} />
          ) : null}
          {call.summary ? (
            <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>
              {call.summary}
            </Typography>
          ) : null}
          <Stack
            spacing={1.25}
            sx={(t) => ({ p: 1.5, borderRadius: `${t.ds.radius.md}px`, bgcolor: t.tokens.surface.sunken })}
          >
            {call.transcript.length === 0 ? (
              <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.tertiary })}>
                {call.live
                  ? 'The words arrive when the call ends. This updates by itself.'
                  : 'Nobody spoke on this call, so there is nothing to read.'}
              </Typography>
            ) : null}
            {call.transcript.map((turn, i) => (
              <Stack key={i} spacing={0.25}>
                <Typography
                  variant="overline"
                  sx={(t) => ({ color: turn.role === 'agent' ? t.tokens.brand.primary : t.tokens.text.tertiary })}
                >
                  {turn.role === 'agent' ? call.agent : call.lead}
                </Typography>
                <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
                  {turn.message}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      ) : null}
    </Modal>
  );
}
